import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FiGrid, FiFileText, FiCalendar, FiUsers, FiCheck, FiBarChart2, FiSettings,
  FiBriefcase, FiFolder, FiUser, FiShield, FiLogIn, FiUserCheck, FiCreditCard,
  FiChevronDown, FiMenu, FiX, FiPieChart, FiSun, FiBriefcase as FiDeal, FiSliders, FiGitMerge
} from "react-icons/fi";
import { fetchPermissions, fetchApprovalStatus } from "../modules/dashboardApi";
import { fetchDealDeskAccess } from "../modules/Dealdesk";

// Route groups (absolute paths so they resolve the same from any page)
const SETTINGS_ROUTES = ["/Settings/Clients", "/Settings/projects", "/users", "/role", "/deal-desk/admin"];

const linkBase =
  "relative flex items-center gap-3 min-h-[38px] rounded-[10px] text-[14px] no-underline transition-colors box-border";
const linkIdle = "text-[#3D4555] hover:bg-[#EEF3FC] hover:text-[#164AA6]";
const linkActive = "bg-[#E3ECFB] text-[#164AA6] font-semibold";

const NavItem = ({ to, icon: Icon, label, collapsed, badge = 0, nested = false, end = false }) => (
  <NavLink
    to={to}
    end={end}
    title={collapsed ? label : undefined}
    aria-label={collapsed ? label : undefined}
    className={({ isActive }) =>
      [
        linkBase,
        isActive ? linkActive : linkIdle,
        collapsed ? "justify-center px-0" : nested ? "pl-[42px] pr-[14px] min-h-[34px] text-[13px]" : "px-[14px]",
      ].join(" ")
    }
  >
    <Icon aria-hidden="true" className={`shrink-0 ${nested ? "text-[16px]" : "text-[18px]"}`} />
    {!collapsed && <span className="truncate">{label}</span>}
    {badge > 0 &&
      (collapsed ? (
        <span aria-hidden="true" className="absolute top-2 right-3 w-2 h-2 rounded-full bg-[#1D5FD1]" />
      ) : (
        <span className="ml-auto bg-[#1D5FD1] text-white text-[11px] font-semibold rounded-full px-2 py-0.5">
          {badge}
        </span>
      ))}
  </NavLink>
);

const NavGroup = ({ icon: Icon, label, open, active, collapsed, onToggle, children }) => (
  <div className="flex flex-col gap-1">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={[
        linkBase,
        "w-full border-0 bg-transparent cursor-pointer text-left font-[inherit]",
        // highlight the parent only when the active child is hidden
        active && (!open || collapsed) ? linkActive : linkIdle,
        collapsed ? "justify-center px-0" : "px-[14px]",
      ].join(" ")}
    >
      <Icon aria-hidden="true" className="shrink-0 text-[18px]" />
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          <FiChevronDown aria-hidden="true" className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </>
      )}
    </button>
    {open && !collapsed && children}
  </div>
);

/**
 * @param {Function} setsidebor     parent callback, receives isDrawerOpen (unchanged contract)
 * @param {number}   approvalCount  optional override for the Approvals badge; otherwise fetched
 */
export const Sidebar = ({ Emp_role, setsidebor, approvalCount }) => {
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [permissions, setPermissions] = useState({}); // { dashboard: true, activity_card: true, ... }
  const [permLoading, setPermLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [canApprove, setCanApprove] = useState(false);   // Org Chart: someone reports to me, or I'm the top
  const [dealDesk, setDealDesk] = useState({ access: false, admin: false }); // dbo.DealDesk_Access

  const roleId = Number(localStorage.getItem("emp_role_id"));
  const path = location.pathname.toLowerCase();
  const settingsActive = SETTINGS_ROUTES.some((r) => path.startsWith(r.toLowerCase()));

  // ── 1. Fetch permissions once on mount ─────────────────────────────
  useEffect(() => {
    const empRoleId = localStorage.getItem("emp_role_id");
    if (!empRoleId) { setPermLoading(false); return; }
    fetchPermissions(empRoleId)
      .then(setPermissions)
      .catch((err) => console.error("Failed to load sidebar permissions:", err))
      .finally(() => setPermLoading(false));
  }, []);

  // ── 1a. Deal Desk access (decided by the server, api/DealDesk/Me) ──
  useEffect(() => {
    fetchDealDeskAccess()
      .then((me) => setDealDesk({ access: !!me.access, admin: !!me.admin }))
      .catch(() => setDealDesk({ access: false, admin: false }));
  }, []);

  // ── 1b. Approvals tab + badge: by the Org Chart, not by role ───────
  // Shown to people with someone under them on the Org Chart, and to the top of the chart.
  // Loaded once, then again when you enter or leave the Approvals page (where the count changes).
  const onApprovals = path.startsWith("/approvals");
  useEffect(() => {
    fetchApprovalStatus(localStorage.getItem("employee_id") || "").then((st) => {
      setCanApprove(st.canApprove);
      setPendingApprovals(st.count);
    });
  }, [onApprovals]);

  // ── 2. Auto-open Settings when landing on one of its pages ─────────
  useEffect(() => {
    if (settingsActive) setSettingsOpen(true);
  }, [settingsActive]);

  useEffect(() => {
    setsidebor?.(isDrawerOpen);
  }, [isDrawerOpen, setsidebor]);

  const can = (key) => !!permissions[key];
  const collapsed = !isDrawerOpen;

  // In collapsed mode a group click expands the drawer and opens the group
  const toggleSettings = () => {
    if (collapsed) {
      setIsDrawerOpen(true);
      setSettingsOpen(true);
    } else setSettingsOpen((o) => !o);
  };

  const asideClass = [
    // Never scrolls: full screen height, items sized to fit (no own scrollbar)
    "sticky top-0 h-screen shrink-0 bg-white border-r border-[#E6E9EF] box-border",
    "flex flex-col gap-4 py-4 overflow-hidden transition-[width] duration-200",
    isDrawerOpen ? "w-[232px] px-4" : "w-[72px] px-3",
  ].join(" ");

  return (
    <aside aria-label="Main" className={asideClass} style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {/* Brand + toggle */}
      <div className={`flex items-center gap-2.5 ${collapsed ? "flex-col" : "px-1.5"}`}>
        <div aria-hidden="true"
          className="w-9 h-9 shrink-0 rounded-[10px] bg-[#1D5FD1] text-white flex items-center justify-center font-bold text-[13px]">
          RCK
        </div>
        {isDrawerOpen && (
          <div className="flex flex-col leading-[1.1] min-w-0">
            <span className="font-bold text-[16px] text-[#0F172A] truncate">RCK Techiees</span>
            <span className="text-[12px] text-[#5B6474]">Timesheet</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => setIsDrawerOpen((o) => !o)}
          aria-label={isDrawerOpen ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={isDrawerOpen}
          className={`${isDrawerOpen ? "ml-auto" : ""} w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-[#E6E9EF] bg-white text-[#5B6474] hover:text-[#1D5FD1] hover:border-[#1D5FD1] cursor-pointer`}
        >
          {isDrawerOpen ? <FiX className="text-[16px]" /> : <FiMenu className="text-[16px]" />}
        </button>
      </div>

      {permLoading ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading menu">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[38px] rounded-[10px] bg-[#F1F3F7] animate-pulse" />
          ))}
        </div>
      ) : (
        <nav className="flex flex-col gap-0.5 min-h-0">
          {/* Dashboard is for everyone (each person sees their own data; admins also get Admin view) */}
          <NavItem to="/dashboard" icon={FiGrid} label="Dashboard" collapsed={collapsed} />
          {can("activity_card") && <NavItem to="/timesheets" icon={FiFileText} label="Activity Card" collapsed={collapsed} />}
          {can("my_activities") && <NavItem to="/my-workitems" icon={FiCalendar} label="My Activities" collapsed={collapsed} />}
          {can("teams_activities") && <NavItem to="/teams-activities" icon={FiUsers} label="Teams Activities" collapsed={collapsed} />}
          {canApprove && (
            <NavItem to="/Approvals" icon={FiCheck} label="Approvals" collapsed={collapsed} badge={approvalCount ?? pendingApprovals} />
          )}
          {can("reports") && <NavItem to="/reports" icon={FiBarChart2} label="Reports" collapsed={collapsed} />}
          {/* Admin only (role 3), or anyone granted a "utilization" permission */}
          {(roleId === 3 || can("utilization")) && (
            <NavItem to="/utilization" icon={FiPieChart} label="Utilization" collapsed={collapsed} />
          )}

          {(roleId === 3 || roleId === 4) && (
            <NavItem to="/org-chart" icon={FiGitMerge} label="Org Chart" collapsed={collapsed} />
          )}
          {/* Admin (3) and HR (4) maintain the company holiday calendar */}
          {(roleId === 3 || roleId === 4) && (
            <NavItem to="/holidays" icon={FiSun} label="Holiday Calendar" collapsed={collapsed} />
          )}
          {/* Deal Desk: only people in dbo.DealDesk_Access; admins also get Settings → Deal Desk Admin */}
          {dealDesk.access && <NavItem to="/deal-desk" end icon={FiDeal} label="Deal Desk" collapsed={collapsed} />}

          {(can("settings") || dealDesk.admin) && (
            <NavGroup icon={FiSettings} label="Settings" open={settingsOpen} active={settingsActive}
              collapsed={collapsed} onToggle={toggleSettings}>
              {can("clients") && <NavItem nested to="/Settings/Clients" icon={FiBriefcase} label="Clients" collapsed={collapsed} />}
              {can("projects") && <NavItem nested to="/Settings/projects" icon={FiFolder} label="Projects" collapsed={collapsed} />}
              {can("users") && <NavItem nested to="/users" icon={FiUser} label="Users" collapsed={collapsed} />}
              {can("role") && <NavItem nested to="/role" icon={FiShield} label="Role" collapsed={collapsed} />}
              {dealDesk.admin && <NavItem nested to="/deal-desk/admin" icon={FiSliders} label="Deal Desk Admin" collapsed={collapsed} />}
            </NavGroup>
          )}

          {/* Not in the permissions table — still role-gated as before */}
          {(roleId === 4 || roleId === 5) && (
            <NavItem to="/Logins/LoginInfo" icon={FiLogIn} label="Login Info" collapsed={collapsed} />
          )}
          {roleId === 5 && (
            <>
              <NavItem to="/employee" icon={FiUserCheck} label="Employee" collapsed={collapsed} />
              <NavItem to="/employeerate" icon={FiCreditCard} label="Rate Card" collapsed={collapsed} />
            </>
          )}
        </nav>
      )}
    </aside>
  );
};